import React, { useCallback, useContext, useMemo } from 'react';
import useQueryParams from 'hooks/useQueryParams';
import { sellingSortOptions, sortOptions as defaultSortOptions } from './utils';
import PortfolioDropdown from '../PortfolioDropdown/PortfolioDropdown.component';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import useCallbackRef from 'hooks/useCallbackRef';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import { FilterOptionType, ListingStats } from './models';
import { aggregateStats } from '../../utils';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { FIRST_PAGE } from 'data/global';
import { Option } from 'models/Global';

import './PortfolioFilterBar.styles.scss';

type PortfolioFilterBarProps = {
  className?: string;
  filterTriggerText?: string;
  filterOptions: FilterOptionType[];
  listingStats: ListingStats;
  showSellingOption?: boolean;
  listingCountForValue: (value: string, listingStats: ListingStats) => string;
};

const PortfolioFilterBar: React.FC<PortfolioFilterBarProps> = ({
  className,
  filterOptions,
  filterTriggerText,
  listingStats,
  showSellingOption,
  listingCountForValue,
}) => {
  const {
    params: { sort, or },
    setQueryParam,
    removeQueryParam,
  } = useQueryParams<{ or: string; sort: string; page: string }>();

  const { isSuperAdmin } = useContext(CurrentUserContext);

  const { t } = useTranslation();

  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>();

  const handleClickOnFilterOption = useCallback(
    (value: string) => {
      value ? setQueryParam('or', value, true) : removeQueryParam('or', true);
      setQueryParam('page', FIRST_PAGE);
      dropdown.setIsOpen(false);
    },
    [dropdown, removeQueryParam, setQueryParam],
  );

  const filterOptionsElements = useMemo(() => {
    return (
      <ul className="portfolio-browse-filter-options">
        {filterOptions.map(({ label, value }) => {
          return (
            <li
              key={label}
              onClick={() => handleClickOnFilterOption(value)}
              className={classNames({
                isActive: value === or,
              })}
            >
              {`${t(label)} (${listingCountForValue(value, listingStats)})`}
            </li>
          );
        }, [])}
      </ul>
    );
  }, [
    filterOptions,
    handleClickOnFilterOption,
    listingCountForValue,
    listingStats,
    or,
    t,
  ]);

  const sortOptions = useMemo(
    () => [
      ...defaultSortOptions,
      ...(showSellingOption && isSuperAdmin ? sellingSortOptions : []),
    ],
    [isSuperAdmin, showSellingOption],
  );

  const handleClickOnSortOption = useCallback(
    (option: Option) => {
      setQueryParam('sort', option.value, true);
      dropdown.setIsOpen(false);
    },
    [dropdown, setQueryParam],
  );

  const sortOptionsElements = useMemo(() => {
    return (
      <ul>
        {sortOptions.map((option) => {
          const { label, value } = option;
          return (
            <li
              key={label}
              onClick={() => {
                handleClickOnSortOption(option);
              }}
              className={classNames({
                isActive: value === sort,
              })}
            >
              {t(label)}
            </li>
          );
        }, [])}
      </ul>
    );
  }, [handleClickOnSortOption, sort, sortOptions, t]);

  const sortTriggerText = useMemo(() => {
    const found = sortOptions.find(({ value }) => value === sort);

    return found?.text || found?.label || t('sortBy');
  }, [sort, sortOptions, t]);

  const showFilterBar = useMemo(
    () => !!(listingStats && !!aggregateStats(listingStats)),
    [listingStats],
  );

  const classes = classNames('portfolio-filter-bar', className);

  return (
    <div className={classes}>
      {showFilterBar && (
        <>
          <PortfolioDropdown triggerText={filterTriggerText} ref={dropdownRef}>
            {filterOptionsElements}
          </PortfolioDropdown>
          <PortfolioDropdown triggerText={sortTriggerText} ref={dropdownRef}>
            {sortOptionsElements}
          </PortfolioDropdown>
        </>
      )}
    </div>
  );
};
export default PortfolioFilterBar;
